import type {
  AccountCreateRequest,
  AccountPatchRequest,
  AccountCreateResponse,
  AccountDeleteResponse,
  AccountMutationResponse,
  AccountsResponse,
  BridgeAccount,
  CommandRequest,
  IntentRecord,
  IntentsResponse,
  TokenRotationResponse,
  TradeIntentRequest,
} from "../../shared/contracts";

export type {
  AccountSnapshot,
  AuthoritativeExecution,
  BridgeAccount,
  BridgeOperation,
  CommandName,
  DeliveryStatus,
  IntentDelivery,
  IntentRecord,
  PendingOrderSnapshot,
  PositionSnapshot,
  SymbolSnapshot,
  TradeSide,
  TradeStrategy,
} from "../../shared/contracts";

export type TradeIntentInput = TradeIntentRequest;
export type CommandInput = CommandRequest;
export type AccountCreateInput = AccountCreateRequest;

export interface ApiConfig {
  baseUrl: string;
  token: string;
  persistToken: boolean;
}

const PREFERENCES_KEY = "tradingbridge.api.config.v2";
const LEGACY_CONFIG_KEY = "tradingbridge.api.config.v1";
const SESSION_TOKEN_KEY = "tradingbridge.api.token.v2";
const IDEMPOTENCY_CACHE_KEY = "tradingbridge.idempotency.pending.v1";
const inMemoryIdempotency = new Map<string, string>();

export function loadApiConfig(): ApiConfig {
  try {
    const stored = JSON.parse(
      localStorage.getItem(PREFERENCES_KEY) ?? localStorage.getItem(LEGACY_CONFIG_KEY) ?? "{}",
    ) as Partial<ApiConfig>;
    const persistToken = stored.persistToken === true;
    let token = persistToken && typeof stored.token === "string"
      ? stored.token
      : safeSessionGet(SESSION_TOKEN_KEY);

    // Older builds stored the token persistently without explicit consent.
    // Migrate it into this tab's session and immediately remove the legacy copy.
    if (!persistToken && !token && typeof stored.token === "string" && stored.token) {
      token = stored.token;
      safeSessionSet(SESSION_TOKEN_KEY, token);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify({
        baseUrl: typeof stored.baseUrl === "string" ? stored.baseUrl : "",
        persistToken: false,
      }));
      localStorage.removeItem(LEGACY_CONFIG_KEY);
    }

    const resolved = resolveBackendUrl(typeof stored.baseUrl === "string" ? stored.baseUrl : "");
    return { baseUrl: resolved.baseUrl, token: token ?? "", persistToken };
  } catch {
    return { baseUrl: "", token: "", persistToken: false };
  }
}

export function saveApiConfig(config: ApiConfig): void {
  const resolved = resolveBackendUrl(config.baseUrl);
  const token = config.token.trim();
  const preferences: Record<string, unknown> = {
    baseUrl: resolved.baseUrl,
    persistToken: config.persistToken,
  };
  safeSessionRemove(SESSION_TOKEN_KEY);
  if (config.persistToken && token) preferences.token = token;
  if (!config.persistToken && token) safeSessionSet(SESSION_TOKEN_KEY, token);
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  localStorage.removeItem(LEGACY_CONFIG_KEY);
}

export function resolveBackendUrl(value: string): { baseUrl: string; origin: string } {
  const trimmed = value.trim();
  if (!trimmed) return { baseUrl: "", origin: window.location.origin };

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error("Backend 網址格式無效。");
  }
  if (url.username || url.password || url.search || url.hash || (url.pathname !== "/" && url.pathname !== "")) {
    throw new Error("Backend 請輸入完整 Origin，不可包含帳密、路徑、查詢或錨點。");
  }
  const localhost = url.hostname === "localhost" || url.hostname.endsWith(".localhost") ||
    url.hostname === "127.0.0.1" || url.hostname === "::1" || url.hostname === "[::1]";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && localhost)) {
    throw new Error("自訂 Backend 必須使用 HTTPS；只有 localhost 可使用 HTTP。");
  }
  const origin = url.origin;
  return origin === window.location.origin
    ? { baseUrl: "", origin }
    : { baseUrl: origin, origin };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "request_failed",
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class TradingBridgeApi {
  private config: ApiConfig;

  constructor(config: ApiConfig) {
    this.config = { ...config, baseUrl: resolveBackendUrl(config.baseUrl).baseUrl, token: config.token.trim() };
  }

  setConfig(config: ApiConfig): void {
    this.config = { ...config, baseUrl: resolveBackendUrl(config.baseUrl).baseUrl, token: config.token.trim() };
  }

  get configured(): boolean {
    return this.config.token.length > 0;
  }

  getAccounts(): Promise<AccountsResponse> {
    return this.request("/api/accounts");
  }

  getIntents(limit = 15): Promise<IntentsResponse> {
    return this.request(`/api/intents?limit=${encodeURIComponent(String(limit))}`);
  }

  createTradeIntent(input: TradeIntentInput): Promise<unknown> {
    return this.request("/api/trade-intents", { method: "POST", body: input, idempotent: true });
  }

  sendCommand(input: CommandInput): Promise<unknown> {
    return this.request("/api/commands", { method: "POST", body: input, idempotent: true });
  }

  createAccount(input: AccountCreateInput): Promise<AccountCreateResponse> {
    return this.request("/api/accounts", { method: "POST", body: input, idempotent: true });
  }

  updateAccount(id: string, input: AccountPatchRequest): Promise<AccountMutationResponse> {
    return this.request(`/api/accounts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: input,
      idempotent: true,
    });
  }

  deleteAccount(id: string): Promise<AccountDeleteResponse> {
    return this.request(`/api/accounts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      idempotent: true,
    });
  }

  rotateBridgeToken(id: string): Promise<TokenRotationResponse> {
    return this.request(`/api/accounts/${encodeURIComponent(id)}/rotate-token`, {
      method: "POST",
      body: {},
      idempotent: true,
    });
  }

  private async request<T>(
    path: string,
    options: { method?: "GET" | "POST" | "PATCH" | "DELETE"; body?: unknown; idempotent?: boolean } = {},
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15_000);
    const method = options.method ?? "GET";
    const bodyJson = options.body === undefined ? undefined : JSON.stringify(options.body);
    const requestSignature = options.idempotent
      ? `${method}\n${path}\n${stableStringify(options.body)}`
      : null;
    const headers = new Headers({ Accept: "application/json" });
    if (this.config.token) headers.set("Authorization", `Bearer ${this.config.token}`);
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    if (requestSignature) headers.set("X-Idempotency-Key", idempotencyKeyFor(requestSignature));

    try {
      const response = await fetch(`${this.config.baseUrl}${path}`, {
        method,
        headers,
        body: bodyJson,
        cache: "no-store",
        signal: controller.signal,
      });
      const contentType = response.headers.get("content-type") ?? "";
      const payload: unknown = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
      // A proxy error or an interrupted body does not prove the mutation failed.
      // Only a complete, successful API response resolves an uncertain attempt.
      if (response.ok && !contentType.includes("application/json")) {
        throw new ApiError("Backend 回應格式異常，請使用相同資料重試。", 0, "invalid_response");
      }
      if (!response.ok) {
        const error = asErrorPayload(payload);
        throw new ApiError(error.message || `HTTP ${response.status}`, response.status, error.code, error.details);
      }
      if (requestSignature) clearIdempotencyKey(requestSignature);
      return payload as T;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("連線逾時，請確認 Backend 狀態。", 0, "timeout");
      }
      throw new ApiError("無法連線 Backend，請檢查 API 網址與網路。", 0, "network_error", error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }
}

function makeIdempotencyKey(): string {
  if (typeof crypto.randomUUID === "function") return `web-${crypto.randomUUID()}`;
  const random = crypto.getRandomValues(new Uint32Array(4));
  return `web-${Date.now()}-${Array.from(random).map((part) => part.toString(16)).join("")}`;
}

function idempotencyKeyFor(signature: string): string {
  const memoryKey = inMemoryIdempotency.get(signature);
  if (memoryKey) return memoryKey;
  const cache = readIdempotencyCache();
  const storedKey = cache[signature];
  if (storedKey) {
    inMemoryIdempotency.set(signature, storedKey);
    return storedKey;
  }
  const key = makeIdempotencyKey();
  cache[signature] = key;
  inMemoryIdempotency.set(signature, key);
  safeSessionSet(IDEMPOTENCY_CACHE_KEY, JSON.stringify(cache));
  return key;
}

function clearIdempotencyKey(signature: string): void {
  inMemoryIdempotency.delete(signature);
  const cache = readIdempotencyCache();
  if (!(signature in cache)) return;
  delete cache[signature];
  if (Object.keys(cache).length) safeSessionSet(IDEMPOTENCY_CACHE_KEY, JSON.stringify(cache));
  else safeSessionRemove(IDEMPOTENCY_CACHE_KEY);
}

function readIdempotencyCache(): Record<string, string> {
  try {
    const parsed = JSON.parse(safeSessionGet(IDEMPOTENCY_CACHE_KEY) ?? "{}") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
  } catch {
    return {};
  }
}

function stableStringify(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`).join(",")}}`;
}

function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

function safeSessionSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* In-memory fallback remains active. */ }
}

function safeSessionRemove(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* Nothing else to clear. */ }
}

function asErrorPayload(value: unknown): { code: string; message: string; details?: unknown } {
  if (!value || typeof value !== "object") return { code: "request_failed", message: String(value || "請求失敗") };
  const object = value as Record<string, unknown>;
  const nested = object.error && typeof object.error === "object"
    ? object.error as Record<string, unknown>
    : object;
  return {
    code: typeof nested.code === "string" ? nested.code : "request_failed",
    message: typeof nested.message === "string" ? nested.message : "請求失敗",
    details: nested.details,
  };
}
